import './styles/tokens.css';
import './styles/base.css';
import './styles/shell.css';
import './styles/surface.css';
import './styles/form.css';
import './styles/nav.css';
import './styles/footer.css';
import './styles/i18n.css';
import './styles/home.css';
import './styles/contact.css';
import './styles/shop.css';
import './styles/cart.css';
import './styles/checkout.css';
import './styles/icons.css';
import './styles/health.css';
import './styles/login.css';
import './styles/sessions.css';
import './styles/me.css';
import './styles/settings.css';
import './styles/comingsoon.css';
import './styles/dialog.css';
import './styles/passkey.css';
import './styles/passkeys.css';
import './styles/recovery.css';
import './styles/error.css';

import { AppShell } from './components/AppShell';
import { HomePage } from './pages/HomePage';
import { ContactPage } from './pages/ContactPage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { LoginPage } from './pages/LoginPage';
import { AuthVerifyPage } from './pages/AuthVerifyPage';
import { RecoveryPage } from './pages/RecoveryPage';
import { ErrorScreen } from './pages/ErrorScreen';
import type { AdminSection } from './components/admin/AdminShell';
import {
  initRouter,
  lazyComponent,
  navigate,
  registerRoute,
  setAuthGuard,
  setNotFound,
  type Component,
} from './router';
import { authInit, isAuthenticated, currentUser } from './services/auth';
import { canViewPath, requiresDesktopPath } from './services/capabilities';
import { features } from './config/features';
import { checkHealth } from './api/client';
import { api } from './services/api';
import { promptPasskeyEnroll } from './components/PasskeyModal';
import { getDeviceClass, setRootDeviceClass } from './utils/viewport';
import { setRootSurface } from './utils/surface';
import { initErrlog } from './utils/errlog';
import { getStoredTheme, setTheme } from './utils/theme';
import { startExpirySweep } from './services/offline';
import { drainSyncQueue } from './services/sync';
import { mergeDeviceCartOnSignIn } from './services/cart';
import { setReturnTo, takeReturnTo } from './utils/returnTo';

function wrap(inner: Component): Component {
  return new AppShell(inner);
}

// OAuth callbacks land on a normal route, not a dedicated page: the
// vfco-auth-package callback redirects to {redirect}?auth=success (the refresh
// cookie is already set, so authInit() below silently exchanges it for a token)
// or {redirect}?auth=error&code=CODE on failure. Normalize the failure to
// /login?error=CODE so LoginPage renders the message, and strip the success
// marker so it doesn't linger in the address bar. Runs before routing.
// Returns true when this load is an OAuth success landing (so the boot can
// resume a stashed returnTo), false otherwise.
function handleOAuthCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  const auth = params.get('auth');
  if (!auth) return false;
  if (auth === 'error') {
    const code = params.get('code') ?? 'OAUTH_PROVIDER_ERROR';
    window.history.replaceState({}, '', `/login?error=${encodeURIComponent(code)}`);
    return false;
  }
  params.delete('auth');
  params.delete('code');
  const qs = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  return true;
}

// On first desktop sign-in for a sensitive role, offer passkey enrollment
//. Best-effort and non-blocking: the GET status poll already ran for
// the SPA, and any failure here must never wedge boot. Phones are exempt, so we
// skip the poll entirely off desktop.
async function maybePromptPasskeyEnroll(): Promise<void> {
  if (!features.passkey) return; // deferred feature off → never offer enrollment
  if (!isAuthenticated() || getDeviceClass() !== 'desktop') return;
  try {
    const status = await api<{ webauthn?: { enrollmentRequired?: boolean } }>('/api/auth/step-up');
    if (status.webauthn?.enrollmentRequired) {
      await promptPasskeyEnroll();
    }
  } catch {
    // No prompt on a failed poll — enrollment can be offered from settings later.
  }
}

// Drain the offline sync queue for the signed-in worker. Best-effort
// and never wedges boot: drainSyncQueue itself no-ops when offline or already
// draining, and a transient/auth failure leaves the queue for the next trigger.
// Fired on boot, on the `online` event, and when the service worker's Background
// Sync wakes the page (the 'sync-drain' message).
function drainSync(): void {
  const workerId = currentUser()?.userId;
  if (!workerId) return;
  void drainSyncQueue(workerId).catch(() => undefined);
}

initErrlog();
setRootDeviceClass();
setRootSurface();
setTheme(getStoredTheme());
// Purge expired offline PII on boot, then hourly. Auth-independent —
// the ~7-day retention is enforced even on a signed-out load.
startExpirySweep();

registerRoute('/', () => wrap(HomePage()));
registerRoute('/contact', () => wrap(new ContactPage()));
registerRoute('/products', () => wrap(new ProductsPage()));
registerRoute('/products/:slug', (params) => wrap(new ProductDetailPage(params.slug)));
// Public legal documents (GDPR-005): Privacy, Cookie, Terms of Sale, and
// Shipping & Returns, each in EN/ES/PT. The (large) trilingual copy lives in each
// page's OWN lazily-loaded module so none of it rides the main bundle; the route
// dynamic-imports that module on first navigation. Public (registered with
// isPublic + added to the auth-guard /legal prefix below) — no sign-in required.
registerRoute('/legal/privacy', () => lazyComponent(() => import('./pages/legal/privacy').then((m) => wrap(m.PrivacyLegalPage()))), true);
registerRoute('/legal/cookies', () => lazyComponent(() => import('./pages/legal/cookies').then((m) => wrap(m.CookiesLegalPage()))), true);
registerRoute('/legal/terms', () => lazyComponent(() => import('./pages/legal/terms').then((m) => wrap(m.TermsLegalPage()))), true);
registerRoute('/legal/shipping-returns', () => lazyComponent(() => import('./pages/legal/shippingReturns').then((m) => wrap(m.ShippingReturnsLegalPage()))), true);
// The Me hub lives behind the "Me" tab (not critical-path) and pulls the store /
// account entry rows + confirm dialog; lazy-load it so none of that rides main.
registerRoute('/me', () => lazyComponent(() => import('./pages/MePage').then((m) => wrap(m.MePage()))));
// Checkout is account-gated — kept OUT of publicPaths below so the auth guard
// requires sign-in before an order can be placed (FE-003.1 / SHOP-004a.1).
registerRoute('/checkout', () => lazyComponent(() => import('./pages/CheckoutPage').then((m) => wrap(new m.CheckoutPage()))));
// /account — the customer account dashboard (orders + addresses + consent),
// lazy-loaded into its own chunk (keeps the main bundle under budget). Not
// public → the guard requires sign-in; capabilities.ts maps it to the `account`
// module, so a role without that cell lands on the forbidden screen.
registerRoute('/account', () =>
  lazyComponent(() => import('./pages/AccountPage').then((m) => wrap(m.AccountPage()))),
);
// Admin store-ops (FE-005) — the catalog screens, lazy-loaded into their own
// chunk together with the standalone AdminShell (NOT the storefront AppShell).
// Kept out of publicPaths → the guard requires sign-in, and capabilities.ts maps
// /catalog* to the `catalog` module, so a role without that cell lands on the
// forbidden screen. /catalog/new is registered before /catalog/:slug so it wins
// the match. wrapAdmin pulls the page + the shell dynamically so no admin code
// rides the main bundle (and the shell import lives in one place, not three).
function adminRoute(page: Promise<Component>, section: AdminSection): Component {
  return lazyComponent(async () => {
    const { AdminShell } = await import('./components/admin/AdminShell');
    return new AdminShell(await page, section);
  });
}

// Admin routes share one shell + one lazy-load pattern, so they register from a
// table rather than one verbose line each (keeps the entry chunk lean). Each
// loader dynamic-imports its page, so every admin surface stays its own chunk.
type RouteParams = Record<string, string>;
const ADMIN_ROUTES: [string, (p: RouteParams) => Promise<Component>, AdminSection][] = [
  ['/overview', () => import('./pages/OverviewPage').then((m) => m.OverviewPage()), 'overview'],
  ['/catalog', () => import('./pages/CatalogAdminPage').then((m) => m.CatalogAdminPage()), 'catalog'],
  ['/catalog/new', () => import('./pages/CatalogEditPage').then((m) => m.CatalogEditPage(null)), 'catalog'],
  ['/catalog/:slug', (p) => import('./pages/CatalogEditPage').then((m) => m.CatalogEditPage(p.slug)), 'catalog'],
  ['/orders', () => import('./pages/OrdersAdminPage').then((m) => m.OrdersAdminPage()), 'orders'],
  ['/orders/:orderNo', (p) => import('./pages/OrderDetailPage').then((m) => m.OrderDetailPage(p.orderNo)), 'orders'],
  ['/customers', () => import('./pages/CustomersAdminPage').then((m) => m.CustomersAdminPage()), 'customers'],
  ['/customers/:userId', (p) => import('./pages/CustomerDetailPage').then((m) => m.CustomerDetailPage(p.userId)), 'customers'],
  ['/admin/settings', () => import('./pages/SettingsAdminPage').then((m) => m.SettingsAdminPage()), 'settings'],
  ['/console', () => import('./pages/console/ConsolePage').then((m) => m.ConsolePage('status')), 'console'],
  ['/console/:lens', (p) => import('./pages/console/ConsolePage').then((m) => m.ConsolePage(p.lens)), 'console'],
];
for (const [path, load, section] of ADMIN_ROUTES) {
  registerRoute(path, (params) => adminRoute(load(params), section));
}
// /settings — the consumer settings hub (Appearance + Security). Account-gated
// (kept out of publicPaths), lazy-loaded into its own chunk. The deep security
// screens register below.
registerRoute('/settings', () => lazyComponent(() => import('./pages/SettingsPage').then((m) => wrap(m.SettingsPage()))));
registerRoute('/settings/devices', () => lazyComponent(() => import('./pages/SessionsPage').then((m) => wrap(m.SessionsPage()))));
// Deferred features (AUTH-004): passkey settings + lost-passkey recovery are
// registered only when their flag is on. Off → the route is never registered,
// so it is unreachable (falls through to the not-found screen).
if (features.passkey) {
  registerRoute('/settings/passkeys', () => lazyComponent(() => import('./pages/PasskeysPage').then((m) => wrap(m.PasskeysPage()))));
}
registerRoute('/login', () => new LoginPage(), true);
registerRoute('/auth/verify', () => new AuthVerifyPage(), true);
if (features.recovery) {
  registerRoute('/recovery', () => new RecoveryPage(), true);
}

setNotFound(() => wrap(new ErrorScreen({ kind: 'notFound' })));
setAuthGuard((path) => {
  // Public storefront surfaces (home, the catalog + product detail, contact)
  // and the auth entry points are open to signed-out visitors. Everything else
  // (the account/Me hub, settings) requires auth. Product detail is a prefix
  // (/products/<slug>), so it is matched separately from the exact-path set.
  const publicPaths = new Set(['/', '/contact', '/products', '/login', '/auth/verify']);
  if (features.recovery) publicPaths.add('/recovery');
  // The public legal documents (/legal/...) are open to signed-out visitors — a
  // prefix match covers all four without listing each.
  if (publicPaths.has(path) || path.startsWith('/products/') || path.startsWith('/legal/')) {
    return { allowed: true };
  }
  if (!isAuthenticated()) {
    // Stash the intended path so sign-in returns here (e.g. /checkout).
    setReturnTo(path);
    return { allowed: false, redirectTo: '/login' };
  }
  // Authenticated: mirror the server RBAC policy so a module this
  // user's role + device class can't reach lands on the friendly forbidden
  // screen in place — rather than a dead nav target or an empty shell. Advisory
  // only: the server re-authorizes every protected call, so this never grants
  // access, it only avoids dead ends.
  if (!canViewPath(path)) {
    return { allowed: false, render: () => wrap(new ErrorScreen({ kind: 'forbidden' })) };
  }
  // Device key gate the capability mirror can't express: some admin surfaces are
  // desktop-only (Console for all staff; Customers for non-admins; the Compliance
  // lens under Console). Off a phone/tablet, render the friendly "use a desktop"
  // screen in place rather than let the page mount, call its API, and dead-end on
  // a 403 DESKTOP_REQUIRED. Advisory — the server still enforces the pin.
  if (requiresDesktopPath(path)) {
    return { allowed: false, render: () => wrap(new ErrorScreen({ kind: 'desktopRequired' })) };
  }
  return { allowed: true };
});

const app = document.getElementById('app');
if (app) {
  void (async () => {
    const oauthReturn = handleOAuthCallback();
    await authInit();
    // A visitor who filled an anonymous cart before signing in: fold it into
    // their server cart now that auth is established (no-op when signed out or
    // when there's nothing to merge). Covers boot-with-cookie + the OAuth return.
    // Start it concurrently with router init so a normal boot isn't blocked; the
    // OAuth-return resume below awaits it so a resumed /checkout doesn't read the
    // server cart mid-merge and flash empty. Best-effort — never wedges boot.
    const mergeDone = mergeDeviceCartOnSignIn().catch(() => undefined);
    initRouter(app);
    // After an OAuth sign-in, resume the gated page the visitor was headed to
    // (e.g. /checkout) if the guard stashed one (magic-link resumes in AuthVerifyPage).
    if (oauthReturn && isAuthenticated()) {
      const back = takeReturnTo();
      if (back) {
        await mergeDone; // cart folded in before the resumed page loads
        navigate(back);
      }
    }
    void checkHealth();
    void maybePromptPasskeyEnroll();

    // Sync any mutations captured offline: once now (a reload after an
    // offline session may have a queue), and again whenever the network returns.
    drainSync();
    window.addEventListener('online', drainSync);

    if ('serviceWorker' in navigator) {
      // Background Sync wakes the page on reconnect via this message (sw.ts) —
      // the page holds the token + worker key the worker can't, so it drains.
      navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if ((event.data as { type?: string } | null)?.type === 'sync-drain') drainSync();
      });
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Service worker registration is best-effort; failure leaves the
        // app fully functional online.
      });
    }
  })();
}
